/**
 * nc-page-embeds.js
 *
 * Runs the <script> tags that merchants paste into a Shopify page body
 * (Admin -> Online Store -> Pages -> Content) when that body is rendered by
 * the `nc-page-content` snippet.
 *
 * The snippet server-renders the body inside an inert <template> so the
 * browser does not execute embed scripts while parsing. This runner then
 * inserts the markup and executes the scripts one at a time, in source order:
 *
 *  - external scripts finish loading before the next script runs
 *  - inline scripts run exactly as authored; if one fails only because it
 *    re-declares a global that another script on the page already owns
 *    (for example an app embed's `var config` vs. a pasted `const config`),
 *    it is re-run inside a function scope so the names cannot collide
 *  - document.write() from an embed is redirected to the embed's position
 *    instead of wiping the document
 *  - sections re-rendered by the theme editor are processed again
 */
(function () {
  if (window.__ncPageEmbeds) return;
  window.__ncPageEmbeds = true;

  var TEMPLATE_SELECTOR = 'template[data-nc-page-content]';
  var REDECLARATION = /already been declared|redeclaration of|duplicate variable|cannot redeclare/i;
  var JS_TYPES = ['', 'text/javascript', 'application/javascript', 'text/ecmascript', 'application/ecmascript', 'module'];

  var queue = Promise.resolve();
  var current = null;
  var nativeWrite = document.write;
  var nativeWriteln = document.writeln;
  var shimDepth = 0;

  function isJavaScript(script) {
    var type = (script.getAttribute('type') || '').trim().toLowerCase();
    return JS_TYPES.indexOf(type) !== -1;
  }

  function copyAttributes(from, to) {
    for (var i = 0; i < from.attributes.length; i++) {
      to.setAttribute(from.attributes[i].name, from.attributes[i].value);
    }
  }

  function redirectWrite(chunks) {
    var html = Array.prototype.join.call(chunks, '');
    if (current && current.parentNode) {
      current.insertAdjacentHTML('afterend', html);
    } else {
      console.warn('[nc-page-embeds] Ignored document.write() outside of an embed script.');
    }
  }

  function installWriteShim() {
    if (shimDepth++ > 0) return;
    document.write = function () {
      redirectWrite(arguments);
    };
    document.writeln = function () {
      redirectWrite(arguments);
      redirectWrite(['\n']);
    };
  }

  function removeWriteShim() {
    if (--shimDepth > 0) return;
    document.write = nativeWrite;
    document.writeln = nativeWriteln;
  }

  function runExternal(source, target) {
    return new Promise(function (resolve) {
      var script = document.createElement('script');
      copyAttributes(source, script);
      var blocking = !source.hasAttribute('async') && !source.hasAttribute('defer');
      script.async = !blocking;
      var finish = function () {
        if (current === script) current = null;
        resolve();
      };
      script.addEventListener('load', finish, { once: true });
      script.addEventListener('error', finish, { once: true });
      current = script;
      target.replaceWith(script);
      if (!blocking) resolve();
    });
  }

  function runInline(source, target, wrapInFunction) {
    var script = document.createElement('script');
    copyAttributes(source, script);
    var code = source.textContent;
    script.textContent = wrapInFunction ? '(function () {\n' + code + '\n})();' : code;

    var collision = null;
    var onError = function (event) {
      var message = (event.error && event.error.message) || event.message || '';
      if (REDECLARATION.test(message)) {
        collision = message;
        event.preventDefault();
      }
    };

    window.addEventListener('error', onError);
    current = script;
    try {
      target.replaceWith(script); // classic inline scripts execute synchronously here
    } catch (error) {
      // Some engines surface the script's own exception from the insertion call.
      var message = (error && error.message) || String(error);
      if (!REDECLARATION.test(message)) throw error;
      collision = message;
    } finally {
      window.removeEventListener('error', onError);
      current = null;
    }

    return { element: script.parentNode ? script : target, collision: collision };
  }

  function insertMarkup(template) {
    if (!template.parentNode) return null;
    var fragment = template.content.cloneNode(true);
    var scripts = Array.prototype.slice.call(fragment.querySelectorAll('script'));
    var jobs = scripts.map(function (script) {
      var placeholder = document.createComment('nc-page-embed');
      script.parentNode.replaceChild(placeholder, script);
      return { script: script, placeholder: placeholder };
    });
    template.replaceWith(fragment);
    return jobs;
  }

  function runJob(job) {
    var source = job.script;
    var placeholder = job.placeholder;
    if (!placeholder.parentNode) return; // removed by an earlier script

    if (!isJavaScript(source)) {
      placeholder.replaceWith(source);
      return;
    }
    if (source.hasAttribute('src')) {
      return runExternal(source, placeholder);
    }

    var result = runInline(source, placeholder, false);
    if (result.collision) {
      console.warn(
        '[nc-page-embeds] Re-running an embed script inside a function scope because another script on this page already owns that global name: ' +
          result.collision
      );
      runInline(source, result.element, true);
    }
  }

  function processTemplate(template) {
    var jobs = insertMarkup(template);
    if (!jobs || !jobs.length) return Promise.resolve();

    installWriteShim();
    var chain = Promise.resolve();
    jobs.forEach(function (job) {
      chain = chain.then(function () {
        return runJob(job);
      });
    });
    return chain.then(removeWriteShim, function (error) {
      removeWriteShim();
      console.error('[nc-page-embeds] Embed script failed:', error);
    });
  }

  function processAll(root) {
    var templates = (root || document).querySelectorAll(TEMPLATE_SELECTOR);
    Array.prototype.forEach.call(templates, function (template) {
      queue = queue.then(function () {
        return processTemplate(template);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      processAll(document);
    });
  } else {
    processAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelectorAll) processAll(event.target);
  });
})();
